// SPDX-License-Identifier: BUSL-1.1

/**
 * Job table column definitions for the agent-detail Jobs tab.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 */

import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { providerBadgeClass } from '@/components/pi-session-panel';
import { formatTimeAgo } from './helpers';
import type { Job } from './types';

export const jobColumns: ColumnDef<Job>[] = [
  {
    accessorKey: 'id',
    header: 'Job ID',
    cell: ({ row }) => (
      <span className="inline-flex cursor-pointer items-center gap-1 font-mono text-xs text-blue-400 hover:text-blue-300">
        {row.original.id.slice(0, 16)}...
        <span className="text-neutral-600">↗</span>
      </span>
    ),
  },
  {
    accessorKey: 'trigger',
    header: 'Trigger',
    cell: ({ row }) => (
      <span className="text-sm text-neutral-300">
        {row.original.trigger}
        {row.original.triggerSource && (
          <span className="ml-1 text-neutral-500">({row.original.triggerSource})</span>
        )}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const statusColors: Record<string, string> = {
        pending: 'bg-yellow-900/40 text-yellow-400',
        processing: 'bg-blue-900/40 text-blue-400',
        completed: 'bg-green-900/40 text-green-400',
        failed: 'bg-red-900/40 text-red-400',
        // MT-RUN-55: distinct from `failed` — the runner never reviewed it
        // because no agent advertised the right capability/agent_type to
        // claim it. Amber (not red) tells operators the symptom is infra,
        // not code: onboard a runner / fix capability config to drain.
        routing_failed: 'bg-amber-900/40 text-amber-400',
        skipped: 'bg-neutral-700 text-neutral-400',
        cancelled: 'bg-neutral-700 text-neutral-400',
      };
      const colors = statusColors[status] ?? 'bg-neutral-700 text-neutral-400';
      // snake_case → space for display, e.g. "routing_failed" → "routing failed"
      const label = status.replace(/_/g, ' ');
      return (
        <span
          className={`inline-flex rounded px-2 py-0.5 text-xs ${colors}`}
          title={
            status === 'routing_failed'
              ? 'No runner advertises a capability for this agent_type — the job will not be reviewed until one does.'
              : undefined
          }
        >
          {label}
        </span>
      );
    },
  },
  {
    id: 'model',
    header: 'Model',
    cell: ({ row }) => {
      const pi = row.original.outputPayload?.piSession;
      if (!pi?.model) return <span className="text-neutral-600">-</span>;
      return (
        <Badge className={providerBadgeClass(pi.provider)}>{pi.model}</Badge>
      );
    },
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: ({ row }) => {
      const piCost = row.original.outputPayload?.piSession?.estimatedCostUsd;
      if (piCost != null) {
        return (
          <span className="font-mono text-xs text-green-400">
            ${piCost.toFixed(4)}
          </span>
        );
      }
      const fallback = row.original.cost;
      if (fallback != null) {
        const n = typeof fallback === 'string' ? parseFloat(fallback) : fallback;
        if (!Number.isNaN(n)) {
          return (
            <span className="font-mono text-xs text-neutral-300">
              ${n.toFixed(4)}
            </span>
          );
        }
      }
      return <span className="text-neutral-600">-</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => (
      <span className="text-neutral-400">{formatTimeAgo(row.original.createdAt)}</span>
    ),
  },
  {
    accessorKey: 'completedAt',
    header: 'Completed',
    cell: ({ row }) => (
      <span className="text-neutral-400">{formatTimeAgo(row.original.completedAt)}</span>
    ),
  },
  {
    accessorKey: 'error',
    header: 'Error',
    cell: ({ row }) => (
      <span className="max-w-xs truncate text-sm text-red-400" title={row.original.error ?? ''}>
        {row.original.error ?? '-'}
      </span>
    ),
  },
];
